import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  UserPlus,
  CheckCircle2,
  Clock,
  PauseCircle,
  User,
  Phone,
  Mail,
  Search,
  SearchX,
  Play,
  Pause,
  Send,
  ShieldAlert,
} from "lucide-react";
import {
  getTeamUsers,
  createTeamUser,
  activateTeamUser,
  deactivateTeamUser,
  resendTeamInvite,
} from "../../../api/team";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage, apiBodyMessages, mapValidationErrors } from "../../../utils/apiErrorMessage";
import { cn } from "../../../utils/cn";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useAuth from "../../../hooks/useAuth";
import { roleRoutes } from "../../../utils/roles";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { Card } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Badge } from "../../../components/ui/Badge";
import { EmptyState } from "../../../components/ui/EmptyState";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { Skeleton } from "../../../components/ui/Skeleton";

// Roles de comercio que ven esta pantalla. Debe coincidir con menuConfig.
const CLIENT_MANAGER_ROLE_ID = "66edc31513059690d19d6eaf";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROW_GRID = "grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_130px_150px]";
const INITIAL_FORM = { name: "", lastName: "", email: "", phone: "" };

// Largos que acepta el backend (CreateTeamUserDto). Se validan también acá para
// no mandar un alta que ya sabemos que va a rebotar con 400.
const NAME_MAX = 80;
const PHONE_MIN = 6;
const PHONE_MAX = 30;

/**
 * Restricción de class-validator ("phone must be longer than or equal to 6
 * characters") → texto para el input. El primer token es la propiedad y se
 * descarta: el error ya se pinta debajo de su campo. Lo que no calza con
 * ninguna restricción conocida se devuelve crudo, sin inventarle traducción.
 * @param {string} message - mensaje crudo del backend
 * @param {(key: string, opts?: object) => string} t
 * @returns {string}
 */
function translateInviteConstraint(message, t) {
  const rest = String(message || "").replace(/^\S+\s*/, "").trim();
  const min = rest.match(/^must be longer than or equal to (\d+) characters$/);
  if (min) return t("Mínimo {{min}} caracteres", { min: min[1] });
  const max = rest.match(/^must be shorter than or equal to (\d+) characters$/);
  if (max) return t("Máximo {{max}} caracteres", { max: max[1] });
  if (/^must be an email$/.test(rest)) return t("Email inválido");
  if (/^should not be empty$/.test(rest)) return t("Este dato es obligatorio.");
  return message;
}

// Estado derivado: activo (aceptó) | pendiente (invitado, sin aceptar) | inactivo (desactivado).
function teamStatus(u) {
  if (u.active) return "active";
  if (u.newUser) return "pending";
  return "inactive";
}

function FormField({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>
        {label}
        {required && <span className="text-status-error"> *</span>}
      </FieldLabel>
      {children}
      {error ? <span className="text-xs text-status-error">{error}</span> : null}
    </div>
  );
}

function TeamLoadingSkeleton({ isMobile }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">{t("Cargando equipo…")}</span>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-2 rounded-lg border border-border bg-bg p-3">
            <div className="flex items-start justify-between gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-6 w-12" />
            </div>
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
      <Skeleton className="h-10 rounded-lg max-w-[480px] mb-4" />
      <div className={isMobile ? "flex flex-col gap-3" : "rounded-xl border border-border bg-bg overflow-hidden"}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-b-0">
            <Skeleton className="w-11 h-11 rounded-full shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TeamManagement() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user, isLoading: authLoading } = useAuth();

  const isCommerceUser =
    user?.role === roleRoutes.client || user?.role === CLIENT_MANAGER_ROLE_ID;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getTeamUsers();
    if (error) {
      showToast(error.message || t("Error al cargar el equipo"), false);
    } else {
      setMembers(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isCommerceUser) fetchMembers();
    else setLoading(false);
  }, [fetchMembers, isCommerceUser]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    const name = form.name.trim();
    const lastName = form.lastName.trim();
    const phone = form.phone.trim();
    if (!name) errs.name = t("Nombre es obligatorio");
    else if (name.length > NAME_MAX) errs.name = t("Máximo {{max}} caracteres", { max: NAME_MAX });
    if (!lastName) errs.lastName = t("Apellido es obligatorio");
    else if (lastName.length > NAME_MAX) errs.lastName = t("Máximo {{max}} caracteres", { max: NAME_MAX });
    if (!form.email.trim()) errs.email = t("Email es obligatorio");
    else if (!EMAIL_REGEX.test(form.email)) errs.email = t("Email inválido");
    if (!phone) errs.phone = t("Teléfono es obligatorio");
    else if (phone.length < PHONE_MIN) errs.phone = t("Mínimo {{min}} caracteres", { min: PHONE_MIN });
    else if (phone.length > PHONE_MAX) errs.phone = t("Máximo {{max}} caracteres", { max: PHONE_MAX });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openModal = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const { data, error } = await createTeamUser({
      name: form.name.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
    });
    setSubmitting(false);

    if (error) {
      // El backend devuelve { message, field } en 409 (email/teléfono duplicado).
      if (error.field) {
        setErrors((prev) => ({ ...prev, [error.field]: error.message }));
        showToast(error.message || t("No se pudo crear el usuario"), false);
        return;
      }
      // 400 del ValidationPipe: `message` llega como array de restricciones (una
      // por campo). Se marca cada input con su texto traducido y el toast
      // resume; si algún mensaje no calzó con ningún campo, se muestra el texto
      // del backend, que es el único que puede explicar qué pasó. api/team.js
      // devuelve el body crudo, así que esto se lee con los helpers.
      const messages = apiBodyMessages(error);
      const { errors: fieldErrors, unmatched } = mapValidationErrors(messages, {
        name: "name",
        lastName: "lastName",
        email: "email",
        phone: "phone",
      });
      const translated = Object.entries(fieldErrors).reduce((acc, [key, message]) => {
        acc[key] = translateInviteConstraint(message, t);
        return acc;
      }, {});
      if (Object.keys(translated).length > 0) setErrors((prev) => ({ ...prev, ...translated }));
      showToast(
        messages.length > 0 && unmatched.length === 0
          ? t("Revisa los campos marcados")
          : apiBodyMessage(error) ?? t("No se pudo crear el usuario"),
        false,
      );
      return;
    }

    // Éxito: aviso claro según si el correo de invitación salió o no.
    showToast(data?.message || t("Usuario invitado"), data?.invite?.sent !== false);
    setModalOpen(false);
    fetchMembers();
  };

  const handleToggle = async (member) => {
    setBusyId(member._id);
    const action = member.active ? deactivateTeamUser : activateTeamUser;
    const { error } = await action(member._id);
    setBusyId(null);
    if (error) {
      showToast(error.message || t("Error al cambiar estado"), false);
    } else {
      showToast(member.active ? t("Usuario desactivado") : t("Usuario activado"), true);
      // Actualizamos solo la fila afectada para no colapsar la tabla al skeleton
      // (fetchMembers pondría loading=true y reemplazaría todo por placeholders).
      setMembers((prev) =>
        prev.map((m) => (m._id === member._id ? { ...m, active: !member.active } : m)),
      );
    }
  };

  const handleResend = async (member) => {
    setBusyId(member._id);
    const { data, error } = await resendTeamInvite(member._id);
    setBusyId(null);
    if (error) {
      showToast(error.message || t("No se pudo reenviar la invitación"), false);
    } else {
      showToast(
        data?.invite?.sent === false
          ? t("No pudimos enviar el correo. Revisa la dirección.")
          : t("Invitación reenviada"),
        data?.invite?.sent !== false,
      );
    }
  };

  const stats = useMemo(() => {
    let active = 0;
    let pending = 0;
    let inactive = 0;
    members.forEach((m) => {
      const s = teamStatus(m);
      if (s === "active") active += 1;
      else if (s === "pending") pending += 1;
      else inactive += 1;
    });
    return { total: members.length, active, pending, inactive };
  }, [members]);

  const filtered = useMemo(() => {
    let list = members;
    if (cardFilter) list = list.filter((m) => teamStatus(m) === cardFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          `${m.name} ${m.lastName}`.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q) ||
          m.phone?.includes(q),
      );
    }
    return list;
  }, [members, search, cardFilter]);

  const statCards = [
    { key: "total", filter: null, tone: "brand", Icon: Users, label: t("Total"), value: stats.total },
    { key: "active", filter: "active", tone: "success", Icon: CheckCircle2, label: t("Activos"), value: stats.active },
    { key: "pending", filter: "pending", tone: "info", Icon: Clock, label: t("Pendientes"), value: stats.pending },
    { key: "inactive", filter: "inactive", tone: "neutral", Icon: PauseCircle, label: t("Inactivos"), value: stats.inactive },
  ];

  const Avatar = ({ status }) => (
    <div
      className={cn(
        "w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border",
        status === "active"
          ? "bg-status-success-soft text-status-success border-status-success/30"
          : status === "pending"
          ? "bg-status-warning-soft text-status-warning border-status-warning/30"
          : "bg-bg-muted text-fg-muted border-border",
      )}
    >
      <User className="w-5 h-5" />
    </div>
  );

  const StatusBadge = ({ status }) => {
    if (status === "active") return <Badge tone="success" dot>{t("Activo")}</Badge>;
    if (status === "pending") return <Badge tone="warning" dot>{t("Pendiente")}</Badge>;
    return <Badge tone="neutral" dot>{t("Inactivo")}</Badge>;
  };

  // Acción por estado: activo→desactivar, pendiente→reenviar, inactivo→activar.
  const RowAction = ({ member, fullWidth }) => {
    const status = teamStatus(member);
    if (status === "pending") {
      return (
        <Button
          size="sm"
          variant="outline"
          fullWidth={fullWidth}
          loading={busyId === member._id}
          onClick={(e) => {
            e.stopPropagation();
            handleResend(member);
          }}
          iconLeft={<Send className="w-4 h-4" />}
        >
          {t("Reenviar")}
        </Button>
      );
    }
    return (
      <Button
        size="sm"
        variant={member.active ? "outline" : "primary"}
        fullWidth={fullWidth}
        loading={busyId === member._id}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle(member);
        }}
        iconLeft={member.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      >
        {member.active ? t("Desactivar") : t("Activar")}
      </Button>
    );
  };

  // Mientras la sesión resuelve, mostramos el skeleton: evita el flash de
  // "Sin acceso" (user aún null) y el de onboarding vacío antes del fetch.
  if (authLoading) {
    return (
      <div className="w-full min-w-0 mt-3">
        <TeamLoadingSkeleton isMobile={isMobile} />
      </div>
    );
  }

  if (!isCommerceUser) {
    return (
      <div className="w-full min-w-0 mt-3">
        <Card>
          <EmptyState
            icon={<ShieldAlert />}
            title={t("Sin acceso")}
            description={t("Esta sección es solo para usuarios de comercio.")}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 mt-3">
      <PageHeader
        icon={<Users className="w-6 h-6 text-fg-muted" />}
        title={t("Usuarios")}
        subtitle={t("Invita y gestiona a los usuarios de tu comercio")}
        actions={
          <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={openModal}>
            {t("Invitar usuario")}
          </Button>
        }
        className="mb-6"
      />

      {loading ? (
        <TeamLoadingSkeleton isMobile={isMobile} />
      ) : members.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users />}
            title={t("Aún no tienes usuarios")}
            description={t("Invita a un colega de tu comercio para que gestione los envíos contigo.")}
            action={
              <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={openModal}>
                {t("Invitar al primer usuario")}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {statCards.map((card) => {
              const Icon = card.Icon;
              const isActive = cardFilter === card.filter;
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={() => setCardFilter((prev) => (prev === card.filter ? null : card.filter))}
                  aria-pressed={isActive}
                  className={cn(
                    "summary-card text-left",
                    `summary-card--${card.tone}`,
                    isActive && "summary-card--active",
                  )}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <Icon className="w-[1.125rem] h-[1.125rem]" />
                    </span>
                    <span className="summary-card__value">{card.value}</span>
                  </div>
                  <div className="summary-card__label">{card.label}</div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="relative flex-1 max-w-[480px] min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-fg-muted pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Buscar por nombre, email o teléfono...")}
                className="pl-10"
              />
            </div>
            <span className="text-[13px] text-fg-muted whitespace-nowrap">
              {search || cardFilter
                ? t("{{count}} de {{total}} usuarios", { count: filtered.length, total: members.length })
                : t("{{total}} usuarios", { total: members.length })}
            </span>
          </div>

          {filtered.length === 0 ? (
            <Card>
              <EmptyState
                icon={<SearchX />}
                title={t("Sin resultados")}
                description={
                  search
                    ? t('No se encontraron usuarios para "{{q}}"', { q: search })
                    : t("No hay usuarios que coincidan con el filtro seleccionado.")
                }
              />
            </Card>
          ) : isMobile ? (
            <div className="flex flex-col gap-3">
              {filtered.map((member) => {
                const status = teamStatus(member);
                return (
                  <Card key={member._id} padding="sm">
                    <div className="flex items-start gap-3">
                      <Avatar status={status} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm text-fg truncate">
                            {member.name} {member.lastName}
                          </div>
                          <StatusBadge status={status} />
                        </div>
                        <div className="text-[13px] text-fg-muted truncate">{member.email}</div>
                        <div className="mt-2 flex items-center gap-1.5 text-[13px] text-fg-muted">
                          <Phone className="w-4 h-4" />
                          {member.phone}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex justify-end">
                      <RowAction member={member} />
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-bg overflow-hidden" role="table">
              <div
                role="row"
                className={cn("grid items-center gap-4 px-5 py-3 bg-bg-muted border-b border-border", ROW_GRID)}
              >
                {[t("Usuario"), t("Teléfono"), t("Estado"), t("Acción")].map((h, i) => (
                  <div
                    key={h}
                    role="columnheader"
                    className={cn(
                      "text-[11px] font-semibold text-fg-muted uppercase tracking-wider",
                      i === 3 && "text-right",
                    )}
                  >
                    {h}
                  </div>
                ))}
              </div>
              {filtered.map((member) => {
                const status = teamStatus(member);
                return (
                  <div
                    key={member._id}
                    role="row"
                    className={cn(
                      "grid items-center gap-4 px-5 py-3 border-b border-border last:border-b-0 hover:bg-bg-muted/50 transition-colors",
                      ROW_GRID,
                    )}
                  >
                    <div role="cell" className="flex items-center gap-3 min-w-0">
                      <Avatar status={status} />
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-fg truncate">
                          {member.name} {member.lastName}
                        </div>
                        <div className="text-[13px] text-fg-muted truncate inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 shrink-0" />
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <div role="cell" className="flex items-center gap-1.5 text-[13px] text-fg-muted min-w-0">
                      <Phone className="w-4 h-4 shrink-0" />
                      <span className="truncate">{member.phone}</span>
                    </div>
                    <div role="cell">
                      <StatusBadge status={status} />
                    </div>
                    <div role="cell" className="flex justify-end">
                      <RowAction member={member} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Invite modal */}
      <Modal
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        size="md"
        title={t("Invitar usuario")}
        footer={
          <div className="flex items-center justify-end w-full gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={submitting}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleSubmit} loading={submitting} iconLeft={<Send className="w-4 h-4" />}>
              {submitting ? t("Enviando…") : t("Enviar invitación")}
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-fg-muted mb-4">
          {t("Le enviaremos un correo para que active su cuenta y elija su contraseña.")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t("Nombre")} required error={errors.name}>
            <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} invalid={!!errors.name} />
          </FormField>
          <FormField label={t("Apellido")} required error={errors.lastName}>
            <Input value={form.lastName} onChange={(e) => handleChange("lastName", e.target.value)} invalid={!!errors.lastName} />
          </FormField>
          <FormField label={t("Email")} required error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              invalid={!!errors.email}
              placeholder="colega@tucomercio.com"
            />
          </FormField>
          <FormField label={t("Teléfono")} required error={errors.phone}>
            <Input
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              invalid={!!errors.phone}
              placeholder="+56912345678"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
